<template>
  <form class="demo-form" @submit.prevent="handleSubmit">
    <div class="form-heading">
      <span>CrediCore</span>
      <h2 :id="idPrefix + '-title'">{{ title }}</h2>
      <p>{{ text }}</p>
    </div>

    <div class="form-grid">
      <label v-if="showProductSelect">
        <span>Producto</span>
        <select
          v-if="showProductSelect"
          v-model="form.product_id"
          :id="idPrefix + '-product'"
          :disabled="productsLoading"
          required
        >
          <option disabled value="">{{ productsLoading ? 'Cargando productos...' : 'Selecciona un producto' }}</option>
          <option v-for="product in productOptions" :key="product.id" :value="product.id">
            {{ product.name }}
          </option>
        </select>
      </label>
      <label>
        <span>Nombre</span>
        <input v-model.trim="form.nombre" :id="idPrefix + '-nombre'" type="text" autocomplete="given-name" required>
      </label>
      <label>
        <span>Apellido</span>
        <input v-model.trim="form.apellido" :id="idPrefix + '-apellido'" type="text" autocomplete="family-name" required>
      </label>
      <label>
        <span>Email</span>
        <input v-model.trim="form.email" :id="idPrefix + '-email'" type="email" autocomplete="email" required>
      </label>
      <label>
        <span>Telefono</span>
        <div class="phone-row">
          <input
            v-model.trim="countryQuery"
            :id="idPrefix + '-codigo'"
            class="phone-code"
            type="text"
            autocomplete="off"
            :list="idPrefix + '-country-codes'"
            required
            @input="handleCountryInput"
            @blur="resetInvalidCountry"
          >
          <datalist :id="idPrefix + '-country-codes'">
            <option
              v-for="country in countryCodes"
              :key="country.code + country.dial_code"
              :value="formatCountryCode(country)"
              :label="country.name"
            />
          </datalist>
          <input
            v-model="form.telefono"
            :id="idPrefix + '-telefono'"
            type="tel"
            inputmode="numeric"
            pattern="[0-9]*"
            autocomplete="tel"
            required
            @input="handlePhoneInput"
          >
        </div>
      </label>
      <label>
        <span>Tipo de documento</span>
        <select v-model="form.tipo_documento" :id="idPrefix + '-tipo-documento'" required>
          <option value="DNI">DNI</option>
          <option value="RTN">RTN</option>
          <option value="Pasaporte">Pasaporte</option>
        </select>
      </label>
      <label>
        <span>No. documento</span>
        <input v-model.trim="form.no_documento" :id="idPrefix + '-documento'" type="text" required>
      </label>
      <label>
        <span>Empresa</span>
        <input v-model.trim="form.nombre_empresa" :id="idPrefix + '-empresa'" type="text" autocomplete="organization" required>
      </label>
      <label>
        <span>Tamano de empresa</span>
        <select v-model="form.tamanio_empresa" :id="idPrefix + '-tamanio'" required>
          <option value="1-10">1-10</option>
          <option value="11-50">11-50</option>
          <option value="51-200">51-200</option>
          <option value="201+">201+</option>
        </select>
      </label>
      <label>
        <span>Como te enteraste?</span>
        <select v-model="form.fuente" :id="idPrefix + '-fuente'" required>
          <option disabled value="">Selecciona una opcion</option>
          <option value="Facebook">Facebook</option>
          <option value="Instagram">Instagram</option>
          <option value="Google">Google</option>
          <option value="Amigo">Amigo</option>
          <option value="Otro">Otro</option>
        </select>
      </label>
      <label class="full">
        <span>Sitio web</span>
        <input
          v-model.trim="form.website_empresa"
          :id="idPrefix + '-website'"
          type="text"
          inputmode="url"
          autocomplete="url"
          placeholder="example.com"
        >
      </label>
      <label class="full">
        <span>Mensaje</span>
        <textarea v-model.trim="form.mensaje" :id="idPrefix + '-mensaje'" rows="4" required></textarea>
      </label>
    </div>

    <p v-if="successMessage" class="form-alert success">{{ successMessage }}</p>
    <p v-if="errorMessage" class="form-alert error">{{ errorMessage }}</p>

    <button class="submit-button" type="submit" :disabled="submitting">
      <i :class="submitting ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-paper-plane'"></i>
      {{ submitting ? 'Enviando...' : 'Solicitar demo' }}
    </button>
  </form>
</template>

<script>
import countryCodes from '@/docs/countyCodes.json'

const defaultCountry = countryCodes.find((country) => country.code === 'HN') || countryCodes[0]

export default {
  name: 'DemoLeadFormContent',
  props: {
    idPrefix: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    text: {
      type: String,
      required: true,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    successMessage: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
    initialMessage: {
      type: String,
      default: 'Interesada en una demo',
    },
    showProductSelect: {
      type: Boolean,
      default: false,
    },
    productOptions: {
      type: Array,
      default: () => [],
    },
    productsLoading: {
      type: Boolean,
      default: false,
    },
    initialProductId: {
      type: [Number, String],
      default: '',
    },
  },
  emits: ['submit-lead'],
  data() {
    return {
      countryCodes,
      selectedCountry: defaultCountry,
      countryQuery: this.formatCountryCode(defaultCountry),
      form: this.defaultForm(),
    }
  },
  methods: {
    formatCountryCode(country) {
      return `${country.dial_code} - ${country.code}`
    },
    defaultForm() {
      return {
        product_id: this.initialProductId || '',
        tipo_documento: 'DNI',
        no_documento: '',
        nombre: '',
        apellido: '',
        email: '',
        codigo_telefono: '+504',
        telefono: '',
        nombre_empresa: '',
        website_empresa: '',
        tamanio_empresa: '11-50',
        fuente: '',
        mensaje: this.initialMessage,
      }
    },
    findCountryFromQuery() {
      const query = this.countryQuery.toLowerCase()

      return this.countryCodes.find((country) => this.formatCountryCode(country).toLowerCase() === query)
    },
    handleCountryInput() {
      const country = this.findCountryFromQuery()

      if (country) {
        this.selectedCountry = country
        this.form.codigo_telefono = country.dial_code
      }
    },
    resetInvalidCountry() {
      const country = this.findCountryFromQuery()

      if (country) {
        this.selectedCountry = country
        this.form.codigo_telefono = country.dial_code
      }

      this.countryQuery = this.formatCountryCode(this.selectedCountry)
    },
    handlePhoneInput() {
      this.form.telefono = this.form.telefono.replace(/\D/g, '')
    },
    handleSubmit() {
      this.resetInvalidCountry()
      this.handlePhoneInput()

      this.$emit('submit-lead', { ...this.form }, () => {
        this.form = this.defaultForm()
        this.selectedCountry = defaultCountry
        this.countryQuery = this.formatCountryCode(defaultCountry)
      })
    },
  },
  watch: {
    initialProductId(newValue) {
      this.form.product_id = newValue || ''
    },
  },
}
</script>
